import React, {FC, useEffect, useRef, useState} from 'react'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {fetchData, toasterrormsg, toastsuccessmsg} from '../../reusable'
import {Tr} from '../../_metronic/i18n/Tr'

// entity types accepted by public/updateMediaSortOrder
export type MediaType =
  | 'hotel'
  | 'room'
  | 'restaurant'
  | 'restaurantmenu'
  | 'spacerental'
  | 'rentablespace'
  | 'tourpackage'
  | 'tours'
  | 'carrental'
  | 'vehicle'

// the media id field is named inconsistently across the apis, so read whichever one is present
const MEDIA_ID_KEYS = [
  'hotelmediaId',
  'roommediaId',
  'restaurantmediaId',
  'restaurantmenumediaId',
  'spacerentalmediaId',
  'rentablespacemediaId',
  'tourpackagemediaId',
  'toursmediaId',
  'tourmediaId',
  'carrentalmediaId',
  'vehiclemediaId',
  'mediaId',
]

const getMediaId = (image: any) => {
  if (!image) return undefined
  const key = MEDIA_ID_KEYS.find((k) => image[k] !== undefined && image[k] !== null)
  return key ? image[key] : undefined
}

const badgeStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
  fontSize: '12px',
  alignSelf: 'end',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0',
  top: '7%',
  right: '-3%',
}

type SortableImageProps = {
  id: string
  src: string
  index: number
  reorderMode: boolean
  onRemove: () => void
}

// single draggable/sortable preview tile
const SortableImage: FC<SortableImageProps> = ({id, src, index, reorderMode, onRemove}) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id,
    disabled: !reorderMode,
  })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: reorderMode ? 'grab' : 'default',
    touchAction: reorderMode ? 'none' : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='col-lg-2 col-4 d-flex flex-column mb-5'
      {...(reorderMode ? attributes : {})}
      {...(reorderMode ? listeners : {})}
    >
      {reorderMode ? (
        <span className='btn btn-primary p-1 px-2 rounded-circle' style={badgeStyle}>
          {index + 1}
        </span>
      ) : (
        <button
          type='button'
          className='btn btn-danger p-1 px-2 rounded-circle'
          style={badgeStyle}
          onClick={onRemove}
        >
          X
        </button>
      )}
      <div style={{maxWidth: 'fit-content'}} className='p-2 shadow rounded-1'>
        <img src={src} width='100%' className='rounded-1' draggable={false} />
      </div>
    </div>
  )
}

export type MediaReorder = ReturnType<typeof useMediaReorder>

type UseMediaReorderArgs = {
  type: MediaType
  inputImage: any[]
  setInputImage: (updater: any) => void
  uploadedImage: any[]
  setUploadedImage: (images: any) => void
}

/**
 * Drag & drop ordering for a photo gallery.
 * Newly attached files are reordered in local state; already uploaded images are
 * reordered and persisted through public/updateMediaSortOrder.
 */
const useMediaReorder = ({
  type,
  inputImage,
  setInputImage,
  uploadedImage,
  setUploadedImage,
}: UseMediaReorderArgs) => {
  const t = Tr()
  const [reorderMode, setReorderMode] = useState(false)
  const [sortingOrder, setSortingOrder] = useState(false)

  // stable id + preview url per selected File, so dnd-kit keeps identity while reordering
  const imageIdRef = useRef(new WeakMap<File, string>())
  const previewUrlRef = useRef(new WeakMap<File, string>())
  const createdUrlsRef = useRef<string[]>([])
  const idCounterRef = useRef(0)

  const files: any[] = inputImage || []
  const uploaded: any[] = uploadedImage || []

  const getImageId = (file: File) => {
    let id = imageIdRef.current.get(file)
    if (!id) {
      idCounterRef.current += 1
      id = `image-${idCounterRef.current}`
      imageIdRef.current.set(file, id)
    }
    return id
  }

  const getPreviewUrl = (file: File) => {
    let url = previewUrlRef.current.get(file)
    if (!url) {
      url = URL.createObjectURL(file)
      previewUrlRef.current.set(file, url)
      createdUrlsRef.current.push(url)
    }
    return url
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  )

  // release preview object urls when leaving the page
  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
      createdUrlsRef.current = []
    }
  }, [])

  const canReorder = files.length + uploaded.length > 1

  // reordering needs at least two images
  useEffect(() => {
    if (!canReorder && reorderMode) {
      setReorderMode(false)
    }
  }, [canReorder, reorderMode])

  const handleNewImageDragEnd = (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return
    setInputImage((prevImages: any) => {
      const list = prevImages || []
      const oldIndex = list.findIndex((file: File) => getImageId(file) === active.id)
      const newIndex = list.findIndex((file: File) => getImageId(file) === over.id)
      if (oldIndex === -1 || newIndex === -1) return prevImages
      return arrayMove(list, oldIndex, newIndex)
    })
  }

  // update media sort order api call
  const updateMediaSortOrderApi = async (reordered: any[], previousOrder: any[]) => {
    var param = {
      type: type,
      mediaIds: reordered.map((image: any) => getMediaId(image)),
      adminId: 0,
    }
    setSortingOrder(true)
    var res = await fetchData('public/updateMediaSortOrder', param)
    setSortingOrder(false)
    if (res.status == '200') {
      toastsuccessmsg(t('Image Order Updated Successfully!'))
    } else {
      // roll back to the previous order when the api fails
      setUploadedImage(previousOrder)
      toasterrormsg(res.message)
    }
  }

  const handleUploadedDragEnd = async (event: DragEndEvent) => {
    const {active, over} = event
    if (!over || active.id === over.id) return

    const oldIndex = uploaded.findIndex((image: any) => String(getMediaId(image)) === active.id)
    const newIndex = uploaded.findIndex((image: any) => String(getMediaId(image)) === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const previousOrder = uploaded
    const reordered = arrayMove(uploaded, oldIndex, newIndex)
    setUploadedImage(reordered)
    await updateMediaSortOrderApi(reordered, previousOrder)
  }

  return {
    t,
    reorderMode,
    setReorderMode,
    sortingOrder,
    canReorder,
    sensors,
    getImageId,
    getPreviewUrl,
    handleNewImageDragEnd,
    handleUploadedDragEnd,
  }
}

// toggle that turns dragging on / off
const ReorderButton: FC<{reorder: MediaReorder}> = ({reorder}) => {
  const {t, reorderMode, setReorderMode, canReorder, sortingOrder} = reorder
  return (
    <>
      <button
        type='button'
        className={`btn ${reorderMode ? 'btn-primary' : 'btn-light'}`}
        disabled={!canReorder || sortingOrder}
        onClick={() => setReorderMode(!reorderMode)}
      >
        {reorderMode ? t('Done Reordering') : t('Reorder')}
      </button>
      {reorderMode && (
        <span className='text-muted fs-7'>
          {t('Drag the images to change their order')}
          {sortingOrder && (
            <span
              className='spinner-border spinner-border-sm mx-2'
              role='status'
              aria-hidden='true'
            ></span>
          )}
        </span>
      )}
    </>
  )
}

// grid of already uploaded images - dropping persists the new order
const UploadedImageGrid: FC<{
  reorder: MediaReorder
  images: any[]
  onRemove: (mediaId: any) => void
}> = ({reorder, images, onRemove}) => {
  const list = images || []
  return (
    <DndContext
      sensors={reorder.sensors}
      collisionDetection={closestCenter}
      onDragEnd={reorder.handleUploadedDragEnd}
    >
      <SortableContext
        items={list.map((image: any) => String(getMediaId(image)))}
        strategy={rectSortingStrategy}
      >
        <div className='row mt-3'>
          {list.map((image: any, index: number) => (
            <SortableImage
              key={String(getMediaId(image))}
              id={String(getMediaId(image))}
              src={image['mediaPath']}
              index={index}
              reorderMode={reorder.reorderMode}
              onRemove={() => onRemove(getMediaId(image))}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

// grid of newly attached files - order is kept in local state until they are saved
const NewImageGrid: FC<{
  reorder: MediaReorder
  files: any[]
  onRemove: (index: number) => void
}> = ({reorder, files, onRemove}) => {
  const list = files || []
  return (
    <DndContext
      sensors={reorder.sensors}
      collisionDetection={closestCenter}
      onDragEnd={reorder.handleNewImageDragEnd}
    >
      <SortableContext
        items={list.map((file: File) => reorder.getImageId(file))}
        strategy={rectSortingStrategy}
      >
        <div className='row mt-3'>
          {list.map((file: any, index: number) => (
            <SortableImage
              key={reorder.getImageId(file)}
              id={reorder.getImageId(file)}
              src={reorder.getPreviewUrl(file)}
              index={index}
              reorderMode={reorder.reorderMode}
              onRemove={() => onRemove(index)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export {useMediaReorder, ReorderButton, UploadedImageGrid, NewImageGrid, SortableImage, getMediaId}
